/**
 * Where the guns are: turns the battles in the player's view into sound sources for the distant
 * artillery rumble. Battles on screen are near and full; battles off screen are dull, quiet and
 * panned towards their side of the map. Pure (no Web Audio), so it is unit-tested.
 */
import type { HexId } from '@friction/engine';

/** Visible world rectangle as reported by the renderer on the bus (`camera`). */
export interface CameraRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface GunSource {
  /** Stereo position −1 (left) … +1 (right). */
  pan: number;
  /** 1 = on screen, down to ~0.1 far away: loudness and brightness of the report. */
  near: number;
  /** Share of the fire coming from this battle. */
  weight: number;
}

export interface Gunfire {
  sources: GunSource[];
  /** Expected reports per second across all sources (0 when all is quiet). */
  rate: number;
}

/** Busy fronts saturate here — the rumble is atmosphere, not a barrage. */
export const MAX_GUN_RATE = 1.8;

/** Night carries sound further and the guns work harder: more and louder reports. */
export const NIGHT_RATE = 1.3;

interface BattleLike {
  hex: HexId;
  attackers: readonly unknown[];
  defenders: readonly unknown[];
}

export function gunfire(
  battles: readonly BattleLike[],
  pos: (hex: HexId) => readonly [number, number],
  cam: CameraRect | null,
  night: boolean,
): Gunfire {
  const sources: GunSource[] = [];
  let total = 0;
  for (const b of battles) {
    const [x, y] = pos(b.hex);
    let near = 0.3;
    let pan = 0;
    if (cam && cam.width > 0 && cam.height > 0) {
      const dx = (x - (cam.x + cam.width / 2)) / (cam.width / 2);
      const dy = (y - (cam.y + cam.height / 2)) / (cam.height / 2);
      const beyond = Math.max(0, Math.max(Math.abs(dx), Math.abs(dy)) - 1);
      near = beyond === 0 ? 1 : Math.max(0.1, 0.55 / (1 + 1.5 * beyond));
      pan = Math.max(-1, Math.min(1, dx)) * 0.75;
    }
    const size = Math.min(6, b.attackers.length + b.defenders.length);
    const weight = (0.6 + 0.2 * size) * near;
    sources.push({ pan, near, weight });
    total += weight;
  }
  const rate = Math.min(MAX_GUN_RATE, 0.3 * total) * (night ? NIGHT_RATE : 1);
  return { sources, rate };
}

/** Pick a source with probability proportional to its weight (`r` uniform in [0, 1)). */
export function pickSource(sources: readonly GunSource[], r: number): GunSource | null {
  const total = sources.reduce((a, s) => a + s.weight, 0);
  if (total <= 0) return null;
  let t = r * total;
  for (const s of sources) {
    t -= s.weight;
    if (t < 0) return s;
  }
  return sources[sources.length - 1];
}
